import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../config/URL";
import GlobalDelete from "../../../components/common/GlobalDelete";
import { exportVisibleColumnsToCSV } from "../../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../../utility/mrtColumnPersistence";

const MODULE_KEY = "LEAVEADMIN";

const LeaveAdmin = ({ selectedCenter }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaveTypeData, setLeaveTypeData] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const canIndex = Boolean(storedScreens?.leaveAdminIndex);
  const canRead = Boolean(storedScreens?.leaveAdminRead);
  const canUpdate = Boolean(storedScreens?.leaveAdminUpdate);

  const showActionColumn = canUpdate;
  const defaultColumnVisibility = {
    leaveReason: false,
    approverName: false,
    fromDate: false,
    leaveType: false,
    leaveTypeId: false,
    noOfDays: false,
    requestDate: false,
    userId: false,
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );
  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "leaveStatus",
        header: "Leave Status",
        Cell: ({ row }) =>
          row.original.leaveStatus === "Approved" ||
          row.original.leaveStatus === "APPROVED" ? (
            <span className="badge badges-Green fw-light">Approved</span>
          ) : row.original.leaveStatus === "Pending" ||
            row.original.leaveStatus === "PENDING" ? (
            <span className="badge badges-orange fw-light">Pending</span>
          ) : row.original.leaveStatus === "Rejected" ||
            row.original.leaveStatus === "REJECTED" ? (
            <span className="badge badges-red fw-light">Rejected</span>
          ) : null,
      },
      { accessorKey: "centerName", header: "Centre Name" },
      { accessorKey: "employeeName", header: "Employee Name" },
      { accessorKey: "leaveType", header: "Leave Type" },
      { accessorKey: "approverName", header: "Approver Name" },
      { accessorKey: "fromDate", header: "From Date" },
      { accessorKey: "leaveReason", header: "Leave Reason" },
      { accessorKey: "leaveTypeId", header: "Leave Type Id" },
      { accessorKey: "noOfDays", header: "No Of Days" },
      { accessorKey: "requestDate", header: "Request Date" },
      { accessorKey: "userId", header: "User Id" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    return [
      baseColumns[0], // S.NO
      {
        accessorKey: "id",
        header: "",
        size: 20,
        enableSorting: false,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  /* -------------------- API CALLS -------------------- */
  const fetchLeaveType = async () => {
    try {
      const response = await api.get(`getAllLeaveSetting`);
      setLeaveTypeData(response.data);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      const url =
        selectedCenter === 0 || selectedCenter === "0"
          ? `getAllUserLeaveRequests`
          : `/getAllUserLeaveRequests?centerId=${selectedCenter}`;

      const response = await api.get(url);
      setData(response.data);
    } catch (error) {
      toast.error("Error Fetching Data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    fetchLeaveType();
  }, [selectedCenter]);

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "LeaveAdminList.csv")
        }
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: canRead
      ? ({ row }) => ({
          onClick: () => navigate(`/leaveadmin/view/${row.original.id}`),
          style: { cursor: "pointer" },
        })
      : undefined,
  });

  return (
    <div className="container-fluid center my-4">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Staffing
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          &nbsp;Leave
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex align-items-center justify-content-between p-1 mb-3"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="active dot"></div>
            </div>
            <span className="text-muted me-2">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Leave
              </span>
            </span>
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {showActionColumn && (
              <Menu
                id="action-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {canUpdate && (
                  <MenuItem
                    onClick={() => navigate(`/leaveadmin/edit/${selectedId}`)}
                    className="text-start mb-0 menuitem-style"
                  >
                    Edit
                  </MenuItem>
                )}
                {canUpdate && (
                  <MenuItem>
                    <GlobalDelete
                      path={`/deleteUserLeaveRequest/${selectedId}`}
                      onDeleteSuccess={fetchLeaveType}
                      onOpen={handleMenuClose}
                    />
                  </MenuItem>
                )}
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaveAdmin;
